import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Plus, Ban, Eye, ClipboardList, Download, Filter } from 'lucide-react';
import * as XLSX from 'xlsx';

// =====================================================================
// Autoavaliação de Controles — a CONSULTA (tela inicial do sub-módulo).
//
// Uma linha por loja/mês, mais recente no topo. O gerente vê só a própria loja
// (recorte no backend, pelo escopoLojaSql); Operacional/Diretoria/Admin veem a
// rede.
//
// ⚠️ AS CANCELADAS APARECEM, tachadas e com o motivo. Esconder o cancelamento
//    faria a lista mentir por omissão — quem lê precisa saber que houve uma
//    resposta anterior, e por que ela foi refeita.
//
// DEM-00011 (2026-09-23): legenda de faixas, linha clicável, paginação e
// exportação em Excel no navegador — no padrão da Avaliação 360
// (Aval360Historico.js), a pedido da Julia. O que É DIFERENTE por natureza
// (a linha cancelada, uma autoverificação por loja/mês, sem tabela de
// evidências) permanece — não faz sentido igualar o que não é igual.
// =====================================================================

const API_BASE = '/api/operacional/autoavaliacao';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const COR = '#475569';

// Cores por faixa de risco — SEMÂNTICAS (dizem o que o número é), a exceção
// registrada em docs/ARQUITETURA.md › Paleta dos módulos.
export const CORES_RISCO = {
  Baixo: { nome: 'Baixo', cor: '#059669', bg: '#d1fae5', icone: '🟢' },
  Medio: { nome: 'Médio', cor: '#b45309', bg: '#fef3c7', icone: '🟡' },
  Critico: { nome: 'Crítico', cor: '#c2410c', bg: '#ffedd5', icone: '🟠' },
  AltoRisco: { nome: 'Alto', cor: '#b91c1c', bg: '#fee2e2', icone: '🔴' },
  NA: { nome: 'N/A', cor: '#475569', bg: '#f1f5f9', icone: '⚪' },
};

export const mesPorExtenso = (anomes) => {
  if (!anomes) return '';
  const [ano, mes] = String(anomes).split('-');
  const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `${nomes[Number(mes) - 1] || mes}/${ano}`;
};

export const dataBr = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('/') : '—');

// Legenda das faixas — espelha autoavaliacao-score.js (limites "até" inclusivos).
const FAIXAS_LEGENDA = [
  { classif: 'Baixo', faixa: 'até 10%' },
  { classif: 'Medio', faixa: '10,01 a 25%' },
  { classif: 'Critico', faixa: '25,01 a 45%' },
  { classif: 'AltoRisco', faixa: 'acima de 45%' },
];

const ITENS_POR_PAGINA = 25;

const AutoavaliacaoConsulta = ({ podePreencher, onNova, onAbrir, recarregarEm, acoesExtras }) => {
  const [linhas, setLinhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [filtroLoja, setFiltroLoja] = useState('');
  const [filtroFormulario, setFiltroFormulario] = useState('');
  const [filtroClassif, setFiltroClassif] = useState('');
  const [filtroAnomes, setFiltroAnomes] = useState('');
  const [pagina, setPagina] = useState(1);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const r = await fetch(`${API_BASE}/historico`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setLinhas(await r.json());
    } catch (e) {
      setErro('Erro ao carregar as autoavaliações.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // `recarregarEm` muda quando uma autoavaliação é gravada ou cancelada — é o
  // que traz a lista de volta atualizada sem o usuário clicar em Atualizar.
  useEffect(() => { carregar(); }, [carregar, recarregarEm]);

  const lojas = useMemo(
    () => [...new Set(linhas.map((l) => l.LOJA))].sort(),
    [linhas]
  );
  // Formulários (versões) presentes na lista — o /historico já traz NOME_VERSAO
  // por linha (a Autoavaliação passou a ser versionada).
  const formularios = useMemo(
    () => [...new Set(linhas.map((l) => l.NOME_VERSAO).filter(Boolean))].sort(),
    [linhas]
  );
  // Classificação efetiva da linha: cancelada tem precedência sobre a faixa de
  // risco (é como a linha aparece na tabela e no Excel). Ordena pela régua
  // (Baixo→Alto), com Cancelada por último.
  const classifEfetiva = (l) => (l.CANCELADO_EM ? 'CANCELADA' : l.CLASSIFICACAO);
  const rotuloClassif = (c) => (c === 'CANCELADA' ? 'Cancelada' : (CORES_RISCO[c]?.nome || c));
  const classifs = useMemo(() => {
    const ordem = ['Baixo', 'Medio', 'Critico', 'AltoRisco', 'NA', 'CANCELADA'];
    return [...new Set(linhas.map(classifEfetiva).filter(Boolean))]
      .sort((a, b) => ordem.indexOf(a) - ordem.indexOf(b));
  }, [linhas]);

  const visiveis = useMemo(
    () => linhas.filter((l) => (!filtroLoja || l.LOJA === filtroLoja)
      && (!filtroFormulario || l.NOME_VERSAO === filtroFormulario)
      && (!filtroClassif || classifEfetiva(l) === filtroClassif)
      && (!filtroAnomes || l.ANOMES === filtroAnomes)),
    [linhas, filtroLoja, filtroFormulario, filtroClassif, filtroAnomes]
  );

  // Volta para a 1ª página sempre que o filtro muda (mesmo padrão da 360).
  useEffect(() => { setPagina(1); }, [filtroLoja, filtroFormulario, filtroClassif, filtroAnomes]);
  const temFiltro = filtroLoja || filtroFormulario || filtroClassif || filtroAnomes;
  const limparFiltros = () => { setFiltroLoja(''); setFiltroFormulario(''); setFiltroClassif(''); setFiltroAnomes(''); };
  const totalPaginas = Math.max(1, Math.ceil(visiveis.length / ITENS_POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const paginados = visiveis.slice((paginaAtual - 1) * ITENS_POR_PAGINA, paginaAtual * ITENS_POR_PAGINA);

  // Exporta a lista FILTRADA, no navegador (mesmo padrão da Avaliação 360).
  const exportarExcel = () => {
    const dados = visiveis.map((l) => ({
      Mês: mesPorExtenso(l.ANOMES), Loja: l.LOJA,
      'Respondida em': dataBr(l.DATA_PREENCHIMENTO), Gerente: l.GERENTE_NOME || '',
      'Não': l.QTD_NAO, Aplicáveis: l.QTD_APLICAVEL, 'N/A': l.QTD_NA,
      'Percentual de Risco (%)': Number(l.PERCENTUAL_RISCO).toFixed(2),
      Classificação: l.CANCELADO_EM ? 'Cancelada' : (CORES_RISCO[l.CLASSIFICACAO]?.nome || l.CLASSIFICACAO),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dados), 'Autoverificações');
    XLSX.writeFile(wb, `Autoverificacoes_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div>
      {/* Cabeçalho + ações — mesmo padrão das outras consultas do Operacional
          (360/SA/Checklist): título em flex:1 e os botões na MESMA linha, sem
          subtítulo. A régua de risco fica na legenda de Faixas logo abaixo.
          `acoesExtras` recebe "Gerenciar versões" do sub-hub (como na 360). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, flex: 1, minWidth: 180, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          Autoverificações gravadas
        </h3>
        <button type="button" onClick={carregar} style={btnSecundario}>
          <RefreshCw size={16} /> Atualizar
        </button>
        {visiveis.length > 0 && (
          <button type="button" onClick={exportarExcel} style={btnSecundario}>
            <Download size={16} /> Exportar Excel
          </button>
        )}
        {acoesExtras}
        {podePreencher && (
          <button type="button" onClick={onNova} style={btnPrimario}>
            <Plus size={16} /> Nova autoverificação
          </button>
        )}
      </div>

      {/* Filtros AO VIVO no padrão das outras consultas do Operacional
          (360/SA/Checklist): CAIXA branca com rótulo por campo + botão Limpar,
          mesma formatação de AvaliacaoSAHistorico.js. Ordem padronizada:
          Loja → Formulário → Classificação → Período (mm/aaaa). */}
      <div style={{ backgroundColor: '#fff', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 14, alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 150px' }}>
          <label style={filtroLbl}>Loja</label>
          <select value={filtroLoja} onChange={(e) => setFiltroLoja(e.target.value)} style={selectEstilo}>
            <option value="">Todas</option>
            {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={filtroLbl}>Formulário</label>
          <select value={filtroFormulario} onChange={(e) => setFiltroFormulario(e.target.value)} style={selectEstilo}>
            <option value="">Todos</option>
            {formularios.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <label style={filtroLbl}>Classificação</label>
          <select value={filtroClassif} onChange={(e) => setFiltroClassif(e.target.value)} style={selectEstilo}>
            <option value="">Todas</option>
            {classifs.map((c) => <option key={c} value={c}>{rotuloClassif(c)}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={filtroLbl}>Período (mm/aaaa)</label>
          <input type="month" value={filtroAnomes} onChange={(e) => setFiltroAnomes(e.target.value)} style={selectEstilo} />
        </div>
        {temFiltro && (
          <button onClick={limparFiltros} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            <Filter size={15} /> Limpar
          </button>
        )}
      </div>

      {/* Legenda das faixas (régua de risco) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 16, paddingLeft: 2 }}>
        <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700 }}>Faixas</span>
        {FAIXAS_LEGENDA.map(({ classif, faixa }) => {
          const c = CORES_RISCO[classif];
          return (
            <span key={classif} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: c.bg, color: c.cor, borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
              {c.nome}<span style={{ fontWeight: 600, opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>{faixa}</span>
            </span>
          );
        })}
      </div>

      {erro && (
        <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>
          {erro}
        </div>
      )}

      {loading ? (
        <p style={{ color: '#64748b' }}>Carregando…</p>
      ) : visiveis.length === 0 ? (
        <div style={{
          background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 12,
          padding: 40, textAlign: 'center', color: '#475569',
        }}>
          <ClipboardList size={44} color="#94a3b8" style={{ marginBottom: 12 }} />
          <h4 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#334155' }}>
            Nenhuma autoverificação gravada
          </h4>
          <p style={{ margin: 0, fontSize: 13 }}>
            {podePreencher
              ? 'Use “Nova autoverificação” para responder o mês corrente.'
              : 'As autoverificações das lojas aparecerão aqui conforme forem respondidas.'}
          </p>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                  <th style={th}>Mês</th>
                  <th style={th}>Loja</th>
                  <th style={th}>Respondida em</th>
                  <th style={th}>Gerente</th>
                  <th style={{ ...th, textAlign: 'right' }}>Risco</th>
                  <th style={th}>Classificação</th>
                  <th style={{ ...th, textAlign: 'center' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {paginados.map((l) => {
                  const cancelada = Boolean(l.CANCELADO_EM);
                  const faixa = CORES_RISCO[l.CLASSIFICACAO] || CORES_RISCO.NA;
                  return (
                    <tr key={l.ID} style={{
                      borderTop: '1px solid #f1f5f9', cursor: 'pointer',
                      opacity: cancelada ? 0.55 : 1,
                      background: cancelada ? '#fafafa' : '#fff',
                    }}
                      onClick={() => onAbrir(l.ID)}
                      onMouseOver={(e) => { if (!cancelada) e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                      onMouseOut={(e) => { e.currentTarget.style.backgroundColor = cancelada ? '#fafafa' : '#fff'; }}>
                      <td style={{ ...td, fontWeight: 700 }}>{mesPorExtenso(l.ANOMES)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{l.LOJA}</td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>
                        {dataBr(l.DATA_PREENCHIMENTO)}
                      </td>
                      <td style={td}>{l.GERENTE_NOME || '—'}</td>
                      {/* Nota: percentual (destaque) + fração de "Não" embaixo — mesmo
                          padrão da coluna "Nota" da Avaliação 360. */}
                      <td style={{ ...td, textAlign: 'right' }}>
                        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <span style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: cancelada ? '#94a3b8' : faixa.cor }}>
                            {Number(l.PERCENTUAL_RISCO).toFixed(2)}%
                          </span>
                          <span style={{ fontSize: 11, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                            {l.QTD_NAO}/{l.QTD_APLICAVEL} não{l.QTD_NA > 0 ? ` (+${l.QTD_NA} N/A)` : ''}
                          </span>
                        </span>
                      </td>
                      <td style={td}>
                        {cancelada ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '3px 9px', borderRadius: 999, background: '#f1f5f9',
                            color: '#64748b', fontWeight: 700, fontSize: 12,
                          }} title={`Cancelada por ${l.CANCELADO_POR_NOME || '—'}: ${l.CANCELADO_MOTIVO || ''}`}>
                            <Ban size={12} /> Cancelada
                          </span>
                        ) : (
                          <span style={{
                            display: 'inline-block', padding: '3px 9px', borderRadius: 999,
                            background: faixa.bg, color: faixa.cor, fontWeight: 800, fontSize: 12,
                          }}>
                            {faixa.icone} {faixa.nome}
                          </span>
                        )}
                      </td>
                      {/* Ação não propaga o clique da linha (senão abriria em dobro). */}
                      <td style={{ ...td, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <button type="button" onClick={() => onAbrir(l.ID)} style={btnLinha}>
                          <Eye size={14} /> Abrir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Rodapé de paginação — mesmo padrão da Avaliação 360. */}
          <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              Página {paginaAtual} de {totalPaginas}
              <span style={{ marginLeft: 10, fontWeight: 600 }}>(Total: {visiveis.length} autoverificaç{visiveis.length === 1 ? 'ão' : 'ões'})</span>
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setPagina((p) => Math.max(p - 1, 1))} disabled={paginaAtual === 1}
                style={btnPag(paginaAtual === 1)}>Anterior</button>
              <button onClick={() => setPagina((p) => Math.min(p + 1, totalPaginas))} disabled={paginaAtual === totalPaginas}
                style={btnPag(paginaAtual === totalPaginas)}>Próxima</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const th = { padding: '10px 14px', fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const td = { padding: '10px 14px', color: '#334155', verticalAlign: 'middle' };
// Mesma formatação da caixa de filtros de AvaliacaoSAHistorico.js (selStyle +
// filtroLbl), para as consultas do Operacional ficarem idênticas.
const filtroLbl = { display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 };
const selectEstilo = { width: '100%', padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', outline: 'none', background: '#fff', color: '#475569', fontSize: 13, fontFamily: 'inherit' };

const btnPrimario = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px',
  background: COR, color: '#fff', border: 'none', borderRadius: 8,
  fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
};
const btnSecundario = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px',
  background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8,
  fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
};
const btnLinha = {
  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px',
  background: '#fff', color: COR, border: `1px solid ${COR}`, borderRadius: 7,
  fontWeight: 700, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', whiteSpace: 'nowrap',
};
const btnPag = (desab) => ({
  padding: '6px 14px', borderRadius: 6, border: '1px solid #cbd5e1', backgroundColor: '#fff',
  color: desab ? '#94a3b8' : '#0f172a', cursor: desab ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
});

export default AutoavaliacaoConsulta;
